import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import autopilotCampaignService from '../../services/autopilotCampaign';
import ApprovalQueue from './ApprovalQueue';

function CampaignDetail({ campaign, onBack, onEdit }) {
    const [analytics, setAnalytics] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('overview');

    const fetchAnalytics = useCallback(async () => {
        setIsLoading(true);
        try {
            const data = await autopilotCampaignService.getCampaignAnalytics(campaign._id);
            setAnalytics(data);
        } catch (error) {
            console.error('Error fetching analytics:', error);
        } finally {
            setIsLoading(false);
        }
    }, [campaign]);

    useEffect(() => {
        if (campaign) {
            fetchAnalytics();
        }
    }, [campaign, fetchAnalytics]);

    const handleStartCampaign = async () => {
        try {
            await autopilotCampaignService.startCampaign(campaign._id);
            toast.success('Campaign started!');
            fetchAnalytics();
        } catch (error) {
            toast.error('Failed to start campaign');
        }
    };

    const handlePauseCampaign = async () => {
        try {
            await autopilotCampaignService.pauseCampaign(campaign._id);
            toast.success('Campaign paused');
            fetchAnalytics();
        } catch (error) {
            toast.error('Failed to pause campaign');
        }
    };

    const handleSourceCandidates = async () => {
        try {
            const result = await autopilotCampaignService.sourceCandidates(campaign._id);
            toast.success(`Found ${result.candidatesFound} candidates!`);
            fetchAnalytics();
        } catch (error) {
            toast.error('Failed to source candidates');
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'active': return 'bg-green-100 text-green-700';
            case 'paused': return 'bg-yellow-100 text-yellow-700';
            case 'completed': return 'bg-blue-100 text-blue-700';
            case 'draft': return 'bg-gray-100 text-gray-700';
            default: return 'bg-gray-100 text-gray-700';
        }
    };

    if (isLoading) {
        return (
            <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
            </div>
        );
    }

    const metrics = analytics?.metrics || campaign.metrics || {};
    const responseRate = metrics.emailsSent > 0
        ? ((metrics.responsesReceived / metrics.emailsSent) * 100).toFixed(1)
        : 0;
    const conversionRate = metrics.candidatesSourced > 0
        ? ((metrics.interviewsScheduled / metrics.candidatesSourced) * 100).toFixed(1)
        : 0;

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-4">
                        <button
                            onClick={onBack}
                            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"
                        >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                            </svg>
                        </button>
                        <div>
                            <h1 className="text-2xl font-bold text-gray-900">{campaign.name}</h1>
                            <p className="text-gray-500">
                                {campaign.targetJob?.title || 'No job selected'} • Created {new Date(campaign.createdAt).toLocaleDateString()}
                            </p>
                        </div>
                    </div>
                    <div className="flex items-center gap-3">
                        <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(campaign.status)}`}>
                            {campaign.status}
                        </span>
                        <button
                            onClick={onEdit}
                            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50"
                        >
                            Edit
                        </button>
                        {campaign.status === 'draft' && (
                            <button
                                onClick={handleStartCampaign}
                                className="px-4 py-2 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700"
                            >
                                Start Campaign
                            </button>
                        )}
                        {campaign.status === 'active' && (
                            <>
                                <button
                                    onClick={handlePauseCampaign}
                                    className="px-4 py-2 bg-yellow-500 text-white rounded-lg font-medium hover:bg-yellow-600"
                                >
                                    Pause
                                </button>
                                <button
                                    onClick={handleSourceCandidates}
                                    className="px-4 py-2 bg-indigo-600 text-white rounded-lg font-medium hover:bg-indigo-700"
                                >
                                    Source Candidates
                                </button>
                            </>
                        )}
                        {campaign.status === 'paused' && (
                            <button
                                onClick={handleStartCampaign}
                                className="px-4 py-2 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700"
                            >
                                Resume
                            </button>
                        )}
                    </div>
                </div>

                {/* Metrics Summary */}
                <div className="grid grid-cols-6 gap-4">
                    <div className="text-center p-4 bg-gray-50 rounded-lg">
                        <div className="text-3xl font-bold text-gray-900">{metrics.candidatesSourced || 0}</div>
                        <div className="text-sm text-gray-500">Sourced</div>
                    </div>
                    <div className="text-center p-4 bg-gray-50 rounded-lg">
                        <div className="text-3xl font-bold text-blue-600">{metrics.emailsSent || 0}</div>
                        <div className="text-sm text-gray-500">Emails Sent</div>
                    </div>
                    <div className="text-center p-4 bg-gray-50 rounded-lg">
                        <div className="text-3xl font-bold text-green-600">{metrics.responsesReceived || 0}</div>
                        <div className="text-sm text-gray-500">Responses</div>
                    </div>
                    <div className="text-center p-4 bg-gray-50 rounded-lg">
                        <div className="text-3xl font-bold text-purple-600">{metrics.interviewsScheduled || 0}</div>
                        <div className="text-sm text-gray-500">Interviews</div>
                    </div>
                    <div className="text-center p-4 bg-blue-50 rounded-lg">
                        <div className="text-3xl font-bold text-blue-700">{responseRate}%</div>
                        <div className="text-sm text-gray-500">Response Rate</div>
                    </div>
                    <div className="text-center p-4 bg-green-50 rounded-lg">
                        <div className="text-3xl font-bold text-green-700">{conversionRate}%</div>
                        <div className="text-sm text-gray-500">Conversion</div>
                    </div>
                </div>
            </div>

            {/* Tabs */}
            <div className="bg-white rounded-lg shadow">
                <div className="border-b border-gray-200">
                    <nav className="flex gap-4 px-6">
                        {[
                            { id: 'overview', label: 'Overview', icon: '📊' },
                            { id: 'queue', label: 'Approval Queue', icon: '✅' },
                            { id: 'activity', label: 'Activity', icon: '📋' },
                            { id: 'settings', label: 'Settings', icon: '⚙️' }
                        ].map(tab => (
                            <button
                                key={tab.id}
                                onClick={() => setActiveTab(tab.id)}
                                className={`py-4 px-2 text-sm font-medium border-b-2 transition-colors ${
                                    activeTab === tab.id
                                        ? 'border-indigo-600 text-indigo-600'
                                        : 'border-transparent text-gray-500 hover:text-gray-700'
                                }`}
                            >
                                <span className="mr-2">{tab.icon}</span>
                                {tab.label}
                            </button>
                        ))}
                    </nav>
                </div>

                <div className="p-6">
                    {/* Overview Tab */}
                    {activeTab === 'overview' && (
                        <div className="space-y-6">
                            {/* Funnel Visualization */}
                            <div className="bg-gradient-to-r from-indigo-50 to-purple-50 p-6 rounded-lg">
                                <h3 className="font-semibold text-gray-900 mb-4">Recruitment Funnel</h3>
                                <div className="flex items-end justify-between h-40">
                                    {[
                                        { label: 'Sourced', value: metrics.candidatesSourced || 0, color: 'bg-indigo-500' },
                                        { label: 'Approved', value: metrics.candidatesApproved || 0, color: 'bg-blue-500' },
                                        { label: 'Emailed', value: metrics.emailsSent || 0, color: 'bg-cyan-500' },
                                        { label: 'Responded', value: metrics.responsesReceived || 0, color: 'bg-green-500' },
                                        { label: 'Interviewed', value: metrics.interviewsScheduled || 0, color: 'bg-blue-500' }
                                    ].map((stage, idx) => {
                                        const maxValue = Math.max(metrics.candidatesSourced || 1, 1);
                                        const height = Math.max((stage.value / maxValue) * 100, 10);
                                        return (
                                            <div key={idx} className="flex flex-col items-center gap-2 flex-1">
                                                <div className="text-lg font-bold text-gray-900">{stage.value}</div>
                                                <div
                                                    className={`w-full max-w-[60px] ${stage.color} rounded-t-lg transition-all`}
                                                    style={{ height: `${height}%` }}
                                                />
                                                <div className="text-xs text-gray-600 text-center">{stage.label}</div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>

                            {/* Campaign Details */}
                            <div className="grid grid-cols-2 gap-6">
                                <div className="bg-gray-50 p-4 rounded-lg">
                                    <h4 className="font-medium text-gray-900 mb-3">Targeting Criteria</h4>
                                    <dl className="space-y-2 text-sm">
                                        <div className="flex justify-between">
                                            <dt className="text-gray-500">Min Match Score</dt>
                                            <dd className="font-medium">{campaign.targetCriteria?.minMatchScore || 75}%</dd>
                                        </div>
                                        <div className="flex justify-between">
                                            <dt className="text-gray-500">Required Skills</dt>
                                            <dd className="font-medium">{campaign.targetCriteria?.skills?.length || 0}</dd>
                                        </div>
                                        <div className="flex justify-between">
                                            <dt className="text-gray-500">Experience Levels</dt>
                                            <dd className="font-medium">
                                                {campaign.targetCriteria?.experienceLevel?.join(', ') || 'Any'}
                                            </dd>
                                        </div>
                                    </dl>
                                </div>
                                <div className="bg-gray-50 p-4 rounded-lg">
                                    <h4 className="font-medium text-gray-900 mb-3">Automation Settings</h4>
                                    <dl className="space-y-2 text-sm">
                                        <div className="flex justify-between">
                                            <dt className="text-gray-500">Auto-approve</dt>
                                            <dd className={`font-medium ${campaign.automation?.autoApprove ? 'text-green-600' : 'text-gray-400'}`}>
                                                {campaign.automation?.autoApprove ? `≥${campaign.automation.autoApproveThreshold}%` : 'Off'}
                                            </dd>
                                        </div>
                                        <div className="flex justify-between">
                                            <dt className="text-gray-500">Auto-send emails</dt>
                                            <dd className={`font-medium ${campaign.automation?.autoSendEmails ? 'text-green-600' : 'text-gray-400'}`}>
                                                {campaign.automation?.autoSendEmails ? 'On' : 'Off'}
                                            </dd>
                                        </div>
                                        <div className="flex justify-between">
                                            <dt className="text-gray-500">Daily limit</dt>
                                            <dd className="font-medium">{campaign.automation?.dailyLimit || 50} emails</dd>
                                        </div>
                                    </dl>
                                </div>
                            </div>

                            {/* Email Sequence */}
                            <div className="bg-gray-50 p-4 rounded-lg">
                                <h4 className="font-medium text-gray-900 mb-3">Email Sequence</h4>
                                <div className="flex items-center gap-4">
                                    <div className="flex items-center gap-2 text-sm">
                                        <span className="w-8 h-8 bg-indigo-100 text-indigo-600 rounded-full flex items-center justify-center font-medium">1</span>
                                        <span>Initial Email</span>
                                    </div>
                                    {campaign.emailSequence?.followUps?.map((followUp, idx) => (
                                        <React.Fragment key={idx}>
                                            <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                            </svg>
                                            <div className="flex items-center gap-2 text-sm">
                                                <span className="w-8 h-8 bg-gray-100 text-gray-600 rounded-full flex items-center justify-center font-medium">
                                                    {idx + 2}
                                                </span>
                                                <span>{followUp.delayDays}d</span>
                                            </div>
                                        </React.Fragment>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Queue Tab */}
                    {activeTab === 'queue' && (
                        <ApprovalQueue campaignId={campaign._id} onRefresh={fetchAnalytics} />
                    )}

                    {/* Activity Tab */}
                    {activeTab === 'activity' && (
                        <div className="space-y-4">
                            <h3 className="font-medium text-gray-900">Recent Activity</h3>
                            {analytics?.recentActivity?.length > 0 ? (
                                <div className="space-y-3">
                                    {analytics.recentActivity.map((activity, idx) => (
                                        <div key={idx} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                                            <div className="w-2 h-2 mt-2 bg-indigo-500 rounded-full" />
                                            <div className="flex-1">
                                                <p className="text-sm text-gray-900">{activity.description}</p>
                                                <p className="text-xs text-gray-500">
                                                    {new Date(activity.timestamp).toLocaleString()}
                                                </p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <p className="text-gray-500 text-sm">No recent activity</p>
                            )}
                        </div>
                    )}

                    {/* Settings Tab */}
                    {activeTab === 'settings' && (
                        <div className="space-y-6">
                            <div className="grid grid-cols-2 gap-6">
                                <div>
                                    <h4 className="font-medium text-gray-900 mb-3">Email Template</h4>
                                    <dl className="space-y-2 text-sm">
                                        <div className="flex justify-between">
                                            <dt className="text-gray-500">Style</dt>
                                            <dd className="font-medium capitalize">{campaign.emailSequence?.initialEmail?.template || 'professional'}</dd>
                                        </div>
                                        <div className="flex justify-between">
                                            <dt className="text-gray-500">Tone</dt>
                                            <dd className="font-medium capitalize">{campaign.emailSequence?.initialEmail?.tone || 'friendly'}</dd>
                                        </div>
                                    </dl>
                                </div>
                                <div>
                                    <h4 className="font-medium text-gray-900 mb-3">Scheduling</h4>
                                    <dl className="space-y-2 text-sm">
                                        <div className="flex justify-between">
                                            <dt className="text-gray-500">Auto-schedule</dt>
                                            <dd className={`font-medium ${campaign.scheduling?.enabled ? 'text-green-600' : 'text-gray-400'}`}>
                                                {campaign.scheduling?.enabled ? 'Enabled' : 'Disabled'}
                                            </dd>
                                        </div>
                                        {campaign.scheduling?.enabled && (
                                            <>
                                                <div className="flex justify-between">
                                                    <dt className="text-gray-500">Provider</dt>
                                                    <dd className="font-medium capitalize">{campaign.scheduling?.provider?.replace('_', ' ')}</dd>
                                                </div>
                                                <div className="flex justify-between">
                                                    <dt className="text-gray-500">Duration</dt>
                                                    <dd className="font-medium">{campaign.scheduling?.interviewDuration || 30} min</dd>
                                                </div>
                                            </>
                                        )}
                                    </dl>
                                </div>
                            </div>
                            <button
                                onClick={onEdit}
                                className="px-4 py-2 bg-indigo-600 text-white rounded-lg font-medium hover:bg-indigo-700"
                            >
                                Edit Campaign Settings
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default CampaignDetail;
